export type LegalDocument = {
  title: string;
  introduction: string;
  sections: { title: string; paragraphs: string[] }[];
};

export const LEGAL_UPDATED = 'October 22, 2026';

const privacy: LegalDocument = {
  title: 'Privacy Policy',
  introduction:
    'This policy explains how this community calendar handles information when you browse events, sign in, subscribe or take part in community activities. Public browsing and public calendar subscriptions do not require an account.',
  sections: [
    {
      title: 'Information used by the calendar',
      paragraphs: [
        'If you sign in, the calendar stores your account name, email address, sign-in provider identifiers, permissions, memberships and preferences. Social sign-in providers share identity information with the calendar; the calendar does not receive your Google, Microsoft or Apple password.',
        'Where enabled, the calendar also stores authentication credentials and security records needed for the sign-in methods you choose, such as password hashes, passkey public keys and multi-factor authentication records. Your passkey private key stays with your authenticator.',
        'Events, uploaded images, RSVP names and party sizes, volunteer signups, poll choices and feedback are stored when you or an authorized organizer submit them. Please avoid including sensitive information or unnecessary details about children.',
      ],
    },
    {
      title: 'How information is used and who can see it',
      paragraphs: [
        'Information is used to run calendars and participation features, authenticate users, enforce access permissions, remember preferences and investigate abuse. Authorized administrators can manage accounts and calendar access.',
        'Public event content and uploaded assets used on public pages can be viewed, copied or indexed by others. Private calendar and event access is restricted by permissions. RSVP details are available to authorized staff and calendar managers, rather than shown as a public attendee list. Authorized organizers can access volunteer and form responses for the activities they manage.',
        'The app does not sell personal information or use it for advertising. Optional analytics are not currently enabled.',
      ],
    },
    {
      title: 'Anonymous feedback',
      paragraphs: [
        'Forms marked anonymous store answers separately from account identities. Please do not put identifying information in an answer if you want it to remain anonymous. A response receipt may be kept in your browser so you can manage your response.',
        'Anonymous does not mean untraceable: hosting providers process network information, and the app uses short-lived abuse-prevention records separately from answers. Free-form text may itself identify its author.',
      ],
    },
    {
      title: 'Cookies and browser storage',
      paragraphs: [
        'The app uses necessary cookies for sign-in, security and management of personal subscription feeds. Browser storage remembers choices such as calendar views, selected calendars, cookie-notice dismissal and map preferences. Blocking storage may prevent sign-in or stop preferences from being remembered.',
        'There are no advertising or analytics cookies added by this app. Cookie details in the footer lets you review the notice and change automatic map loading.',
      ],
    },
    {
      title: 'Service providers, maps and calendar subscriptions',
      paragraphs: [
        'Cloudflare hosts the application and processes stored data and network requests needed to deliver and secure it. An enabled social sign-in provider processes your sign-in under its own privacy policy.',
        'Embedded Google Maps load only after you choose Show map or enable automatic loading. Loading a map sends your request, including network information such as your IP address, to Google, which may use its own cookies. You can turn automatic loading off in Cookie details. Opening an external map or another linked service is governed by that service\u2019s policies.',
        'Subscribing sends the selected event information to your calendar provider or calendar app. Treat a personal subscription URL as a secret, especially when it includes private calendars. Revoking a feed stops future authorized access, but does not erase copies already downloaded by another service or person.',
      ],
    },
    {
      title: 'Retention, changes and deletion',
      paragraphs: [
        'Accounts, calendar content, permissions and security/audit records remain stored to support the service and its administration. Some temporary security and participation records expire automatically. There is no single deletion period covering all records.',
        'You can manage subscriptions and supported participation responses through the app. Contact a calendar administrator to request account deletion or correction of information you cannot change yourself. Deleting an account does not necessarily remove shared event content, de-identified audit history, backups or copies held by calendar providers. Backups and external copies may remain after information is removed from the live app.',
      ],
    },
    {
      title: 'Community use and questions',
      paragraphs: [
        'Account and participation features are intended for adult family members, staff and community organizers. Do not create accounts on behalf of children or submit children\u2019s contact information.',
        'For privacy questions or data requests, contact the calendar administrator through your parent group\u2019s usual contact channel. The date above identifies the current version; changes to the service\u2019s data practices will be reflected on this page.',
      ],
    },
  ],
};

const terms: LegalDocument = {
  title: 'Terms of Use',
  introduction:
    'These terms describe permitted use of this calendar and its event, subscription and participation features for the school community. Please also read the Privacy Policy.',
  sections: [
    {
      title: 'Calendar information and event participation',
      paragraphs: [
        'Schedules, locations, availability and event details can change. Confirm important details with the event organizer. Imported feeds and external links may contain information supplied by other organizations. The calendar is not an emergency notification service.',
        'An RSVP or volunteer signup records your response through the app. It is not a guarantee that an event will take place or that an organizer has made arrangements beyond those described in the event. Update or withdraw your response when your plans change.',
      ],
    },
    {
      title: 'Accounts and restricted access',
      paragraphs: [
        'Use accurate account information and only accounts you are authorized to use. Keep sign-in credentials, response-management links and private subscription URLs secure. Do not share private calendar information with people who are not allowed to see it.',
        'Sign-in alone does not grant staff, editor or administrator rights. Access depends on the calendar\u2019s rules and application permissions. Administrators may restrict or remove access to protect the community, respond to abuse or reflect changed responsibilities.',
      ],
    },
    {
      title: 'Respectful and safe use',
      paragraphs: [
        'Do not harass others, impersonate someone, submit spam or harmful content, upload material you have no right to share, or disclose another person\u2019s private information without permission. Do not attempt to bypass access controls, collect private member information or disrupt the service.',
        'Use adult contact information for participation. Do not create child accounts or include sensitive student information in event pages, uploads, RSVPs or feedback. Anonymous feedback must follow the same community standards as identified responses.',
      ],
    },
    {
      title: 'Content you contribute',
      paragraphs: [
        'You retain your rights to content you submit. By submitting it, you give the service permission to store, process, display and distribute it as needed to operate the selected calendar, event, feed or participation feature, subject to its access settings.',
        'Only submit content you have permission to share, including photographs and information about other people. Authorized organizers may edit or remove content to maintain calendar accuracy, enforce access settings or address inappropriate submissions. Public content can be copied by others, and removing it here cannot recall every external copy.',
      ],
    },
    {
      title: 'Availability and external services',
      paragraphs: [
        'The service is provided for community coordination and may be unavailable during maintenance or failures. Features may change or be disabled. Keep a separate record of information you need independently of the calendar.',
        'External sign-in, map and calendar providers have their own terms and privacy policies. Linking an external event or website does not mean the calendar administrators operate or endorse that service.',
      ],
    },
    {
      title: 'Questions and updates',
      paragraphs: [
        'Contact the calendar administrator through your parent group\u2019s usual contact channel about access, inappropriate content or these terms. The date above identifies this version. Updated terms will be published on this page when the service\u2019s rules change.',
      ],
    },
  ],
};

export function legalDocument(path: string): LegalDocument | null {
  if (path === '/privacy' || path === '/privacy/') return privacy;
  if (path === '/terms' || path === '/terms/') return terms;
  return null;
}
